import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product Card Component with Dark Buttons
const ProductCard = ({ product, onWaitlistJoin }) => {
  const [email, setEmail] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isJoined, setIsJoined] = useState(false);

  const isOutOfStock = product.is_coming_soon || product.inventory_quantity === 0;

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        kit_id: product.id,
        kit_name: product.title
      }).catch(() => {});
      
      setIsJoined(true);
      toast.success(`You're on the waitlist for ${product.title}!`);
      onWaitlistJoin?.(product.id, email);
    } catch (error) {
      toast.success(`You're on the waitlist for ${product.title}!`);
      setIsJoined(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="group relative bg-white border border-gray-200 hover:border-[#C6A87C]/50 overflow-hidden product-card"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <Link to={`/products/${product.id}`}>
        <div className="aspect-[4/3] overflow-hidden bg-white relative">
          <img
            src={product.image_url}
            alt={product.title}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          />
          
          {/* Coming Soon Overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <span className="bg-[#1a1a1a] text-white font-bold uppercase tracking-wider px-4 py-2 text-sm">
                Coming Soon
              </span>
            </div>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className="p-6 border-t border-gray-100">
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">
          {product.vendor || 'Essential Series'}
        </p>
        <Link to={`/products/${product.id}`}>
          <h3 className="font-['Barlow_Condensed'] text-xl font-semibold uppercase tracking-tight mb-2 text-gray-900 hover:text-[#C6A87C] transition-colors">
            {product.title}
          </h3>
        </Link>
        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
          {product.description}
        </p>

        {/* Price */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-2xl font-bold text-gray-900">${product.price}</span>
            <span className="text-sm text-gray-500 ml-1">/day</span>
            <p className="text-xs text-gray-500">
              3-day min (${(parseFloat(product.price) * 3 * 0.85).toFixed(2)} total)
            </p>
          </div>
        </div>

        {/* Button - Dark style */}
        {isOutOfStock ? (
          !isJoined ? (
            !showForm ? (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
                data-testid={`join-waitlist-btn-${product.id}`}
              >
                <Mail className="w-4 h-4 mr-2" />
                Join the Waitlist
              </Button>
            ) : (
              <form onSubmit={handleWaitlistSubmit} className="space-y-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full h-10 px-3 bg-gray-50 border border-gray-200 text-sm placeholder:text-gray-400 focus:border-[#1a1a1a] focus:outline-none transition-colors text-gray-900"
                  data-testid={`waitlist-email-${product.id}`}
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
                  data-testid={`submit-waitlist-${product.id}`}
                >
                  {isSubmitting ? 'Joining...' : 'Get Early Access'}
                </Button>
              </form>
            )
          ) : (
            <div className="flex items-center justify-center gap-2 py-3 bg-gray-100 border border-gray-200">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700 font-semibold uppercase tracking-wider">
                On the Waitlist
              </span>
            </div>
          )
        ) : (
          <Link to={`/products/${product.id}`} className="block">
            <Button
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider"
              data-testid={`rent-now-btn-${product.id}`}
            >
              Rent Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>

      {/* Type Badge */}
      {product.product_type && !isOutOfStock && (
        <span className="absolute top-4 left-4 px-3 py-1 bg-[#1a1a1a] text-white text-xs font-semibold uppercase tracking-widest">
          {product.product_type}
        </span>
      )}
      
      {/* Pro Badge */}
      {product.is_pro && (
        <span className="absolute top-4 right-4 px-3 py-1 bg-[#C6A87C] text-black text-xs font-bold uppercase tracking-widest">
          Pro
        </span>
      )}
    </div>
  );
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waitlistSignups, setWaitlistSignups] = useState({});

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products`);
        // Filter out security deposit product
        const filteredProducts = response.data.filter(p => 
          !p.title?.toLowerCase().includes('security deposit') &&
          !p.handle?.toLowerCase().includes('security-deposit')
        );
        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleWaitlistJoin = (kitId, email) => {
    setWaitlistSignups(prev => ({ ...prev, [kitId]: email }));
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="products-page">
      {/* Header - No Filter Tabs */}
      <div className="px-6 md:px-12 py-12 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto">
          <div>
            <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-4">
              Essential Series
            </p>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-gray-900">
              All Gear
            </h1>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="px-6 md:px-12 py-12">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white border border-gray-200 animate-pulse">
                  <div className="aspect-[4/3] bg-gray-100"></div>
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gray-100 w-1/4"></div>
                    <div className="h-6 bg-gray-100 w-3/4"></div>
                    <div className="h-4 bg-gray-100 w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No products found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
              {products.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onWaitlistJoin={handleWaitlistJoin}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
